import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';

/**
 * Global error handler.
 *
 * Backend / network failures surface as unhandled `HttpErrorResponse`s when a
 * `.subscribe()` has no error callback. Those are not frontend defects, so we
 * log them locally instead of reporting them to Sentry as "unhandled" crashes
 * (they were the noisy "Object captured as exception with keys: error, headers,
 * status, statusText, url" issues). Everything else is forwarded to Sentry as
 * before.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
	// logErrors: when Sentry is not initialised (dev/staging) captureException is a no-op, so without this every
	// frontend exception would disappear silently.
	private readonly sentryHandler = Sentry.createErrorHandler({ logErrors: true });

	handleError(error: any): void {
		// zone.js wraps unhandled promise rejections as { rejection: <thrown> }.
		const actual = error?.rejection ?? error;

		if (actual instanceof HttpErrorResponse) {
			// Still log to the console (same as before) — just don't report to Sentry.
			console.error(`HTTP error ${actual.status} ${actual.url}`, actual);
			return;
		}

		this.sentryHandler.handleError(error);
	}
}
